import _ from 'lodash';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from "react";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import {
  Grid,
  Alert,
  Button,
  Dialog,
  Divider,
  MenuItem,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  TextField
} from "@mui/material";

import { useTranslate } from "src/locales";
import successAnimation from 'src/assets/lottie/success.json';
import { CompleteManualReferenceSubmissionDto } from "src/services/references/references.types";
import {
  useGetReferenceRequestByIdQuery,
  useCompleteManualReferenceSubmissionMutation
} from "src/services/references/references.service";

import AnimationContainer from 'src/components/animation/container';
import {
  RHFTextField
} from 'src/components/hook-form';
import RHFCountryCodeSelector from 'src/components/hook-form/rhf-country-code-selector';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { TenantType } from 'src/types/enums';

type Props = {
  open: boolean;
  referenceRequestId: string;
  onClose: VoidFunction;
};

export enum FlowStep {
  REFEREE_DETAILS = 'referee_details',
  QUESTIONS = 'questions',
  REVIEW = 'review'
}

const STEPS = [
  {
    id: FlowStep.REFEREE_DETAILS,
    label: 'Referee Details',
    description: 'Review and edit referee information'
  },
  {
    id: FlowStep.QUESTIONS,
    label: 'Reference Questions',
    description: 'Fill in the reference questions'
  },
  {
    id: FlowStep.REVIEW,
    label: 'Review & Submit',
    description: 'Review all information before submitting'
  }
];

export default function SubmitReferenceModal({ open, referenceRequestId, onClose }: Props) {

  const { t } = useTranslate();
  const tenantType = useOrgTenant();

  const [view, setView] = useState<'submit' | 'success'>('submit');
  const [flowStep, setFlowStep] = useState<FlowStep>(FlowStep.REFEREE_DETAILS);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [referenceErrors, setReferenceErrors] = useState<{ [key: string]: string }>({});

  const [submitReference, { isLoading: isSubmitting }] = useCompleteManualReferenceSubmissionMutation();
  const { currentData: referenceRequest, isLoading: loadingRequest } = useGetReferenceRequestByIdQuery({
    referenceRequestId
  }, {
    skip: !referenceRequestId
  });

  // Referee details validation schema
  const RefereeDetailsSchema = Yup.object().shape({
    first_name: Yup.string().required(t("validation.required")),
    last_name: Yup.string().required(t("validation.required")),
    email: Yup.string().email(t("validation.email_format")).required(t("validation.required")),
    phone: Yup.string().required(t("validation.required")),
    country_code: Yup.string().required(t("validation.required")),
    relationship: Yup.string().required(t("validation.required")),
    position_title: Yup.string().required(t("validation.required")),
    company_name: Yup.string().required(t("validation.required")),
    referee_position: Yup.string().required(t("validation.required")),
  });

  const defaultValues = useMemo(() => ({
    first_name: referenceRequest?.referee.first_name ?? "",
    last_name: referenceRequest?.referee.last_name ?? "",
    email: referenceRequest?.referee.email ?? "",
    phone: referenceRequest?.referee.phone ?? "",
    country_code: referenceRequest?.referee.country_code ?? "",
    relationship: referenceRequest?.referee.relationship ?? "",
    position_title: referenceRequest?.referee.position_title ?? "",
    company_name: referenceRequest?.referee.company_name ?? "",
    referee_position: referenceRequest?.referee.referee_position ?? ""
  }), [referenceRequest]);

  const methods = useForm({
    resolver: yupResolver(RefereeDetailsSchema),
    defaultValues,
  });

  const {
    trigger,
    getValues,
    reset
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const activeStep = useMemo(() => STEPS.findIndex(step => step.id === flowStep), [flowStep]);

  const orderedQuestions = useMemo(() => {
    if (!referenceRequest) return [];
    return _.sortBy(referenceRequest.questions, ['order']);
  }, [referenceRequest]);

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value
    }));

    if (referenceErrors[questionId] && value.trim()) {
      setReferenceErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateReferenceInput = () => {
    if (!referenceRequest) return false;

    const newErrors: { [key: string]: string } = {};
    referenceRequest.questions.forEach((question) => {
      if (question.required && !answers[question.id]?.trim()) {
        newErrors[question.id] = t("validation.required");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setReferenceErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!referenceRequest) return;

    setReferenceErrors({});

    const submissionData: CompleteManualReferenceSubmissionDto = {
      submissions: referenceRequest.questions.map((question) => ({
        question_id: question.id,
        answer: answers[question.id] || "",
      })),
      referee_details: {
        first_name: getValues("first_name"),
        last_name: getValues("last_name"),
        phone: getValues("phone"),
        country_code: getValues("country_code"),
        relationship: getValues("relationship"),
        position_title: getValues("position_title"),
        company_name: getValues("company_name"),
        referee_position: getValues("referee_position")
      }
    };

    try {
      await submitReference({
        referenceRequestId,
        data: submissionData,
      }).unwrap();

      setView('success');
      if (tenantType === TenantType.Recruiter) {
        enqueueSnackbar(t("applications.references.submit_reference_modal.rec_success_message"), { variant: "success" });
      }
      else if (tenantType === TenantType.Client) {
        enqueueSnackbar(t("applications.references.submit_reference_modal.client_success_message"), { variant: "success" });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("applications.references.submit_reference_modal.submit_error"), { variant: "error" });
    }
  };

  const handleNext = async () => {
    const currentStep = STEPS[activeStep];

    switch (currentStep.id) {
      case FlowStep.REFEREE_DETAILS: {
        const isValid = await trigger();
        if (isValid) {
          setFlowStep(FlowStep.QUESTIONS);
        }
        break;
      }
      case FlowStep.QUESTIONS: {
        const isReferenceValid = validateReferenceInput();
        if (isReferenceValid) {
          setFlowStep(FlowStep.REVIEW);
        }
        break;
      }
      case FlowStep.REVIEW: {
        await handleSubmit();
        break;
      }
      default:
        break;
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setFlowStep(STEPS[activeStep - 1].id);
    }
  };

  const handleClose = () => {
    setView('submit');
    setFlowStep(FlowStep.REFEREE_DETAILS);
    setAnswers({});
    setReferenceErrors({});
    onClose();
  };

  const renderStepContent = () => {
    if (!referenceRequest) return null;

    switch (flowStep) {
      case FlowStep.REFEREE_DETAILS:
        return (
          <FormProvider {...methods}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <RHFTextField
                  name="first_name"
                  label={t("applications.references.submit_reference_modal.labels.first_name")}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <RHFTextField
                  name="last_name"
                  label={t("applications.references.submit_reference_modal.labels.last_name")}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  name="email"
                  label={t("applications.references.submit_reference_modal.labels.email")}
                  fullWidth
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box>
                    <RHFCountryCodeSelector
                      name="country_code"
                      label={t("applications.references.submit_reference_modal.labels.country_code")}
                    />
                  </Box>
                  <Box flexGrow={1}>
                    <RHFTextField
                      name="phone"
                      label={t("applications.references.submit_reference_modal.labels.phone")}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  name="relationship"
                  label={t("applications.references.submit_reference_modal.labels.relationship")}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  name="position_title"
                  label={t("applications.references.submit_reference_modal.labels.position_title")}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  name="company_name"
                  label={t("applications.references.submit_reference_modal.labels.company_name")}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RHFTextField
                  name="referee_position"
                  label={t("applications.references.submit_reference_modal.labels.referee_position")}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </FormProvider>
        );

      case FlowStep.QUESTIONS:

        return (
          <Grid container spacing={3}>
            {orderedQuestions.map((question) => (
              <Grid item xs={12} key={question.id}>
                <Typography variant="subtitle1" gutterBottom>
                  {question.question}
                  {question.required && <span style={{ color: "red" }}> *</span>}
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={answers[question.id] || ""}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  error={!!referenceErrors[question.id]}
                  helperText={referenceErrors[question.id]}
                  placeholder={t("applications.references.submit_reference_modal.answer_placeholder")}
                />
              </Grid>
            ))}
          </Grid>
        );

      case FlowStep.REVIEW:

        return (
          <Stack spacing={3}>
            <Typography variant="h6">
              {t("applications.references.submit_reference_modal.review_title")}
            </Typography>

            <Alert severity="info">
              {t("applications.references.submit_reference_modal.review_notice")}
            </Alert>

            <Divider />

            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("applications.references.submit_reference_modal.referee_details_title")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.first_name")}
                    value={getValues("first_name")}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.last_name")}
                    value={getValues("last_name")}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.email")}
                    value={getValues("email")}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.phone")}
                    value={`${getValues("country_code")} ${getValues("phone")}`}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.relationship")}
                    value={getValues("relationship")}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.position_title")}
                    value={getValues("position_title")}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.company_name")}
                    value={getValues("company_name")}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("applications.references.submit_reference_modal.labels.referee_position")}
                    value={getValues("referee_position")}
                    disabled
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("applications.references.submit_reference_modal.questions_title")}
              </Typography>
              {orderedQuestions.map((question) => (
                <Stack spacing={1} key={question.id}>
                  <Typography variant="body1" fontWeight="medium">
                    {question.question}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {answers[question.id] || t("applications.references.submit_reference_modal.no_answer")}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        );

      default:
        return null;
    }
  };

  const renderNextButton = () => {
    switch (flowStep) {
      case FlowStep.REFEREE_DETAILS:
        return (
          <Button variant="contained" onClick={handleNext}>
            {t("common.next")}
          </Button>
        );
      case FlowStep.QUESTIONS:
        return (
          <Button variant="contained" onClick={handleNext}>
            {t("common.next")}
          </Button>
        );
      case FlowStep.REVIEW:
        return (
          <LoadingButton
            loading={isSubmitting}
            variant="contained"
            onClick={handleNext}
          >
            {t("common.submit")}
          </LoadingButton>
        );
      default:
        return null;
    }
  };

  if (loadingRequest) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!referenceRequest) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent>
          <Alert severity="error">
            {t("applications.references.submit_reference_modal.request_not_found")}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (view === 'success') {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, textAlign: 'center' }}>
          <AnimationContainer height={150} width={150} json={successAnimation} loop={false} />
          <Typography variant="h5" sx={{ mt: 2 }}>
            {t("applications.references.submit_reference_modal.success_title")}
          </Typography>
          <Typography variant="body2">
            {t("applications.references.submit_reference_modal.success_subtitle")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack spacing={2}>
          <Typography variant="h6">
            {t('applications.references.submit_reference_modal.title')}
          </Typography>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {STEPS[activeStep].description}
          </Typography>

          <Box sx={{ minHeight: 300 }}>
            {renderStepContent()}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          {t("common.back")}
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button onClick={handleClose}>
          {t("common.cancel")}
        </Button>

        {renderNextButton()}
      </DialogActions>
    </Dialog>
  );
}